// @ts-nocheck — vendored/mirrored file: logic is byte-faithful to popin-rn and
// verified in its home repo's CI. The portal's tsconfig is stricter
// (noUncheckedIndexedAccess) than upstream's; the engine boundary is type-checked
// via ./derive.ts.
// ═══════════════════════════════════════════════════════════════════════════
// MIRRORED FROM popin-rn — DO NOT EDIT BY HAND.
//   source: smiletime/popin-rn  design/ds-sync/engine/signals.ts
//   ref:    65f24d55aa9d4f5e978ff6697da875767f6cb8b2  (origin/main, fetched 2026-07-03)
// Byte-for-byte copy of the pop.in Theme Studio engine (PURE — no react-native).
// Drift is tracked by the source git SHA in ./provenance.ts (MIRRORED_FROM_SHA);
// re-sync via scripts/resync-theme-engine.sh. See ./README.md.
// ═══════════════════════════════════════════════════════════════════════════
/**
 * signals — the SIGNAL MANIFEST + per-treatment re-expression. This is the
 * "engine-complete" fix for #322 Phase-B-item-2 (the constant-token LEAK).
 *
 * THE LEAK: ~50 semantic "constant" tokens (feedback*, state* presence/mic/
 * match/saved/search, live*, glitch*, currency*, chromeNeutral*, the scrim) ship
 * as GLOBAL `tokens.color` values in tamagui.config.ts — theme-blind, shared by
 * reference across every sub-theme. The engine ramp (anchor-generate.ts) only
 * emits ~46 surface/action/text keys, so on any non-NIGHT treatment these ~50
 * fall back to their NIGHT value → neon greens/reds bleed onto a cream LCD.
 *
 * THE FIX — complete-by-construction: this module is the SINGLE canonical list of
 * every constant token. `buildCellTheme` iterates it so EVERY treatment emits
 * EVERY token. A token missing a policy fails the coverage test (signals.test).
 *
 * THE POLICY (per AJ, issue #322): we "reserve" the ROLE, never the VALUE. A
 * frozen NIGHT-red stamped on a Game Boy LCD is the same illusion-breaking bug as
 * the leak. So each signal is RE-EXPRESSED in the treatment's own material world:
 *   • EMISSIVE (NIGHT) — the shipped neon values verbatim (NIGHT is the reference).
 *   • REFLECTIVE (DAYLIGHT/PAPER/GREEN) — a deep EARTH ink of the SAME HUE,
 *     OKLCH-derived against the substrate, AA-gated. Same meaning, local pixels.
 *
 * PURE DATA — no react-native imports (engine folder stays Node-runnable).
 */
import { hexToOklch, oklchToHex, hexToRgb, wcagContrast } from "./anchor-generate";

// ─────────────────────────────────────────────────────────────────────────
// The substrate shape signals.ts needs (treatments.ts `Substrate` matches it
// structurally — declared here to avoid a circular import).
// ─────────────────────────────────────────────────────────────────────────
export interface ReflectiveSubstrate {
	background: string;
	muted: string;
	card: string;
	elevated: string;
	ink: string;
	inkSecondary: string;
	inkMuted: string;
	border: string;
	borderSubtle: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Color parsing — night values come in #hex / hsl() / hsla() / rgb() / rgba().
// Normalise any of them to { hex (opaque), alpha } so we can read the OKLCH hue.
// ─────────────────────────────────────────────────────────────────────────
function hslToHex(h: number, s: number, l: number): string {
	s /= 100; l /= 100;
	const k = (n: number) => (n + h / 30) % 12;
	const a = s * Math.min(l, 1 - l);
	const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
	const hx = (n: number) => Math.round(255 * n).toString(16).padStart(2, "0");
	return `#${hx(f(0))}${hx(f(8))}${hx(f(4))}`;
}

export interface ParsedColor { hex: string; alpha: number }

/** Parse any css color string we use as a night value → opaque hex + alpha. */
export function parseColor(input: string): ParsedColor {
	const c = input.trim();
	if (c.startsWith("#")) {
		let h = c.slice(1);
		if (h.length === 3) h = h.split("").map((x) => x + x).join("");
		if (h.length === 8) return { hex: `#${h.slice(0, 6)}`, alpha: parseInt(h.slice(6, 8), 16) / 255 };
		return { hex: `#${h.slice(0, 6)}`, alpha: 1 };
	}
	const hsl = c.match(/hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*([\d.]+)\s*)?\)/i);
	if (hsl) {
		return { hex: hslToHex(parseFloat(hsl[1]), parseFloat(hsl[2]), parseFloat(hsl[3])), alpha: hsl[4] !== undefined ? parseFloat(hsl[4]) : 1 };
	}
	const rgb = c.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)/i);
	if (rgb) {
		const hx = (n: string) => Math.round(parseFloat(n)).toString(16).padStart(2, "0");
		return { hex: `#${hx(rgb[1])}${hx(rgb[2])}${hx(rgb[3])}`, alpha: rgb[4] !== undefined ? parseFloat(rgb[4]) : 1 };
	}
	return { hex: "#000000", alpha: 1 };
}

const rgba = (hex: string, a: number): string => {
	const { r, g, b } = hexToRgb(hex);
	return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
};

// ─────────────────────────────────────────────────────────────────────────
// Reflective re-expression primitives.
// ─────────────────────────────────────────────────────────────────────────
const CREAM = "#F4F0E2"; // the on-color a deep reflective ink hosts (matches treatments REFLECTIVE_ON_ACTION)
const DEEP_INK = "#241F17"; // deepest neutral ink fallback (for on-colors on a light signal)

/** A deep EARTH ink of a given hue, against a reflective substrate: pull chroma
 *  to ink-range, sink lightness deep, darken until a cream label clears AA — so
 *  the signal reads as deep spot-colour on paper AND can host a cream on-color.
 *  Achromatic inputs (grays) collapse to a neutral substrate ink. */
function deepInkOfHue(nightHex: string, sub: ReflectiveSubstrate): string {
	const o = hexToOklch(nightHex);
	if (o.C < 0.03) return sub.inkMuted; // achromatic signal (offline/mic-off gray) → paper gray
	const C = Math.min(o.C * 0.7, 0.13);
	let L = 0.46;
	let hex = oklchToHex({ L, C, H: o.H });
	for (let i = 0; i < 48 && wcagContrast(CREAM, hex) < 4.6; i++) {
		L -= 0.012;
		hex = oklchToHex({ L, C, H: o.H });
	}
	return hex;
}

/** Best foreground (cream vs deepest ink) for a re-expressed signal — max WCAG. */
function onColorFor(signalHex: string): string {
	return wcagContrast(CREAM, signalHex) >= wcagContrast(DEEP_INK, signalHex) ? CREAM : DEEP_INK;
}

// ─────────────────────────────────────────────────────────────────────────
// THE MANIFEST — every constant token, its NIGHT (shipped) value, and its role.
// `night` values are copied VERBATIM from tamagui.config.ts tokens.color so the
// NIGHT treatment renders today's pop.in exactly. `parent` links on/tint tokens
// to the signal they derive from. `slot` maps a neutral to a substrate field.
// ─────────────────────────────────────────────────────────────────────────
export type SignalRole = "signal" | "on" | "tint" | "neutral" | "scrim" | "glow";

export interface SignalSpec {
	night: string;
	role: SignalRole;
	parent?: string; // on/tint/glow: the signal token whose hue they follow
	slot?: keyof ReflectiveSubstrate; // neutral: which substrate field to map to
	tintAlpha?: number; // tint: wash opacity over the panel (default reads night alpha)
}

export const SIGNAL_MANIFEST: Record<string, SignalSpec> = {
	// ── glitch / bot terminal ────────────────────────────────────────────
	glitchGreen:  { night: "#00d030", role: "signal" },
	glitchDark:   { night: "#001100", role: "tint", parent: "glitchGreen", tintAlpha: 0.14 },
	glitchBorder: { night: "#003300", role: "tint", parent: "glitchGreen", tintAlpha: 0.32 },
	// ── live indicator ───────────────────────────────────────────────────
	liveGreen: { night: "#88cc00", role: "signal" },
	liveDark:  { night: "#0a1a00", role: "tint", parent: "liveGreen", tintAlpha: 0.16 },
	// ── action.destructive ───────────────────────────────────────────────
	actionDestructive:   { night: "#E5484D", role: "signal" },
	actionOnDestructive: { night: "#FAFAFA", role: "on", parent: "actionDestructive" },
	red10:               { night: "#FF6369", role: "signal" }, // raw Radix red10 (destructive hover/press)
	// ── state.live / location / private ──────────────────────────────────
	stateLive:          { night: "#88CC00", role: "signal" },
	stateOnLive:        { night: "#0A0A0A", role: "on", parent: "stateLive" },
	stateYouAreHere:    { night: "#44FF88", role: "signal" },
	stateOnYouAreHere:  { night: "#06140B", role: "on", parent: "stateYouAreHere" },
	statePrivate:       { night: "#FFB224", role: "signal" },
	stateOnPrivate:     { night: "#1A0F00", role: "on", parent: "statePrivate" },
	// ── presence ─────────────────────────────────────────────────────────
	statePresenceOnline:  { night: "hsl(130, 80%, 45%)", role: "signal" },
	statePresenceIdle:    { night: "#FFB224", role: "signal" },
	statePresenceOffline: { night: "hsl(0, 0%, 45%)", role: "neutral", slot: "inkMuted" },
	// ── state.saved (4-step teal CTA ramp) ───────────────────────────────
	stateSaved:        { night: "hsl(190, 95%, 39%)", role: "signal" },
	stateSavedTint:    { night: "hsl(192, 79.3%, 12.8%)", role: "tint", parent: "stateSaved", tintAlpha: 0.16 },
	stateSavedFill:    { night: "hsl(192, 75.9%, 10.8%)", role: "tint", parent: "stateSaved", tintAlpha: 0.10 },
	stateSavedSurface: { night: "hsl(192, 60%, 7.2%)", role: "tint", parent: "stateSaved", tintAlpha: 0.06 },
	// ── state.unfollowed ─────────────────────────────────────────────────
	stateUnfollowed:     { night: "hsl(275, 80%, 55%)", role: "signal" },
	stateUnfollowedTint: { night: "hsl(275, 79%, 12.8%)", role: "tint", parent: "stateUnfollowed", tintAlpha: 0.16 },
	// ── matchmaking: searching / matched ─────────────────────────────────
	stateSearching:     { night: "#FFAA00", role: "signal" },
	stateOnSearching:   { night: "#1A0D00", role: "on", parent: "stateSearching" },
	stateSearchingTint: { night: "rgba(255, 170, 0, 0.10)", role: "tint", parent: "stateSearching", tintAlpha: 0.12 },
	stateMatched:       { night: "hsl(252, 60%, 57%)", role: "signal" },
	stateOnMatched:     { night: "hsl(0, 0%, 98%)", role: "on", parent: "stateMatched" },
	stateMatchedTint:   { night: "rgba(110, 86, 207, 0.14)", role: "tint", parent: "stateMatched", tintAlpha: 0.14 },
	// ── A/V mic ──────────────────────────────────────────────────────────
	stateMicOn:    { night: "hsl(130, 80%, 45%)", role: "signal" },
	stateOnMicOn:  { night: "#03110A", role: "on", parent: "stateMicOn" },
	stateMicOff:   { night: "hsl(0, 0%, 36%)", role: "neutral", slot: "inkMuted" },
	stateOnMicOff: { night: "hsl(0, 0%, 96%)", role: "on", parent: "stateMicOff" },
	// ── friend request ───────────────────────────────────────────────────
	stateInvited:     { night: "hsl(131, 41%, 46.5%)", role: "signal" },
	stateOnInvited:   { night: "#FAFAFA", role: "on", parent: "stateInvited" },
	stateInvitedTint: { night: "rgba(34, 197, 94, 0.16)", role: "tint", parent: "stateInvited", tintAlpha: 0.16 },
	stateRequested:   { night: "hsl(130, 50%, 32%)", role: "signal" },
	stateOnRequested: { night: "hsl(130, 30%, 92%)", role: "on", parent: "stateRequested" },
	// ── feedback ─────────────────────────────────────────────────────────
	feedbackSuccess:       { night: "#30A46C", role: "signal" },
	feedbackOnSuccess:     { night: "#FAFAFA", role: "on", parent: "feedbackSuccess" },
	feedbackWarning:       { night: "#FFB224", role: "signal" },
	feedbackOnWarning:     { night: "#1A0F00", role: "on", parent: "feedbackWarning" },
	feedbackError:         { night: "#E5484D", role: "signal" },
	feedbackOnError:       { night: "#FAFAFA", role: "on", parent: "feedbackError" },
	feedbackInfo:          { night: "#00A2C7", role: "signal" },
	feedbackOnInfo:        { night: "#FAFAFA", role: "on", parent: "feedbackInfo" },
	feedbackCelebration:   { night: "#D6409F", role: "signal" },
	feedbackOnCelebration: { night: "#FAFAFA", role: "on", parent: "feedbackCelebration" },
	// ── currency identity (POPS) — re-expresses per AJ (no literal freeze) ──
	currencyIdentity:     { night: "#FFD66B", role: "signal" },
	currencyIdentityGlow: { night: "rgba(255, 214, 107, 0.35)", role: "glow", parent: "currencyIdentity" },
	// ── scrim + neutral chrome ───────────────────────────────────────────
	surfaceOverlay:      { night: "rgba(0, 0, 0, 0.45)", role: "scrim" },
	chromeNeutralBg:     { night: "hsl(0, 0%, 11%)", role: "neutral", slot: "muted" },
	chromeNeutralBorder: { night: "hsl(0, 0%, 24%)", role: "neutral", slot: "border" },
};

/** The semantic theme-aware tokens (emitted from the ramp by the engine). Listed
 *  here so the coverage test asserts the FULL contract = ramp + signals. */
export const SEMANTIC_TOKENS = [
	"actionPrimary", "actionPrimaryGlow", "actionOnPrimary", "actionSecondaryBorder",
	"actionOnSecondary", "actionTertiary", "actionOnTertiary",
	"stateScheduled", "stateOnScheduled", "stateEnded", "stateOnEnded", "stateUnread", "stateRead",
	"surfaceBackground", "surfaceMuted", "surfaceCard", "surfaceElevated",
	"textDefault", "textSecondary", "textMuted", "textDisabled",
	"borderDefault", "borderFocus", "borderSubtle",
	"headerBackground", "brandAccent", "brandAccentGlow", "playAccent",
	"color1", "color2", "color3", "color4", "color5", "color6",
	"color7", "color8", "color9", "color10", "color11", "color12",
] as const;

/** The full canonical token contract the coverage test asserts complete. */
export const CONTRACT_TOKENS: string[] = [...SEMANTIC_TOKENS, ...Object.keys(SIGNAL_MANIFEST)];

// ─────────────────────────────────────────────────────────────────────────
// The generators — one per polarity. EMISSIVE = verbatim night; REFLECTIVE =
// re-expressed deep inks against the substrate. Complete by iterating the manifest.
// ─────────────────────────────────────────────────────────────────────────

/** Re-express a sub-theme's IDENTITY accent (its shipped color9 hue) into the
 *  active treatment's world (#325 B2). Reflective → a deep ink of the SAME hue
 *  against the substrate (cyan tab → cool ink, amber → warm ink) so the Play-tab
 *  morph stays distinct + material-honest; emissive → keep the bright identity.
 *  This is the SAME OKLCH re-expression the manifest uses (deepInkOfHue). */
export function reExpressAccent(identityColor: string, sub?: ReflectiveSubstrate): string {
	if (!sub) return identityColor;
	return deepInkOfHue(parseColor(identityColor).hex, sub);
}

/** EMISSIVE (NIGHT): the shipped values verbatim — NIGHT is the reference. */
export function emissiveSignals(): Record<string, string> {
	const out: Record<string, string> = {};
	for (const [token, spec] of Object.entries(SIGNAL_MANIFEST)) out[token] = spec.night;
	return out;
}

/** REFLECTIVE: re-express every signal in the substrate's world. Two passes —
 *  signals/neutrals first (so on/tint can read their resolved parent). */
export function reflectiveSignals(sub: ReflectiveSubstrate): Record<string, string> {
	const out: Record<string, string> = {};
	// pass 1 — signals + neutrals + scrim
	for (const [token, spec] of Object.entries(SIGNAL_MANIFEST)) {
		if (spec.role === "signal") {
			out[token] = deepInkOfHue(parseColor(spec.night).hex, sub);
		} else if (spec.role === "neutral") {
			out[token] = sub[spec.slot ?? "inkMuted"];
		} else if (spec.role === "scrim") {
			out[token] = rgba(sub.ink, parseColor(spec.night).alpha); // dim toward the world's ink, keep night opacity
		}
	}
	// pass 2 — on-colors + tints + glows (need the resolved parent signal)
	for (const [token, spec] of Object.entries(SIGNAL_MANIFEST)) {
		if (spec.role === "on") {
			const parentHex = spec.parent ? out[spec.parent] : undefined;
			out[token] = parentHex ? onColorFor(parentHex) : CREAM;
		} else if (spec.role === "tint") {
			const parentHex = spec.parent ? out[spec.parent] : sub.ink;
			out[token] = rgba(parentHex, spec.tintAlpha ?? parseColor(spec.night).alpha);
		} else if (spec.role === "glow") {
			out[token] = rgba(spec.parent ? out[spec.parent] : sub.ink, 0); // no halo on a reflective panel
		}
	}
	return out;
}
